import { useState, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import {
  Activity, Search, X, ChevronLeft, ChevronRight,
  AlertCircle, AlertTriangle, Info, Bug, RefreshCw,
  Download, Shield, User, Layers,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { clsx } from 'clsx';

// ── Types ─────────────────────────────────────────────────────────────────────
type Level = 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';
type Tab   = 'activity' | 'system';

interface SysLog {
  id: string; level: Level; source: string; message: string;
  stack_trace: string | null; tenant_id: string | null;
  metadata: Record<string, unknown> | null; created_at: string;
}

interface AuditEvent {
  event_id: string; tenant_id: string; tenant_name: string | null;
  actor_user_id: string; actor_name: string | null; actor_role: string;
  actor_ip: string | null; action_type: string; resource_type: string;
  resource_id: string; metadata: Record<string, unknown> | null;
  created_at: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const LEVEL_CONFIG: Record<Level, { label: string; color: string; bg: string; icon: React.FC<any> }> = {
  ERROR: { label: 'Error', color: 'text-red-700',    bg: 'bg-red-100',    icon: AlertCircle   },
  WARN:  { label: 'Warn',  color: 'text-yellow-700', bg: 'bg-yellow-100', icon: AlertTriangle },
  INFO:  { label: 'Info',  color: 'text-blue-700',   bg: 'bg-blue-100',   icon: Info          },
  DEBUG: { label: 'Debug', color: 'text-gray-600',   bg: 'bg-gray-100',   icon: Bug           },
};

const ACTION_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  CREATE:           { label: 'Create',         color: 'text-green-700',  bg: 'bg-green-100'  },
  UPDATE:           { label: 'Update',         color: 'text-blue-700',   bg: 'bg-blue-100'   },
  DELETE:           { label: 'Delete',         color: 'text-red-700',    bg: 'bg-red-100'    },
  LOGIN:            { label: 'Login',          color: 'text-indigo-700', bg: 'bg-indigo-100' },
  LOGOUT:           { label: 'Logout',         color: 'text-slate-600',  bg: 'bg-slate-100'  },
  LOGIN_FAILED:     { label: 'Login Failed',   color: 'text-red-700',    bg: 'bg-red-100'    },
  VIEW:             { label: 'View',           color: 'text-gray-600',   bg: 'bg-gray-100'   },
  DATA_EXPORT:      { label: 'Export',         color: 'text-purple-700', bg: 'bg-purple-100' },
  PASSWORD_CHANGE:  { label: 'Pwd Change',     color: 'text-orange-700', bg: 'bg-orange-100' },
  MFA_SETUP:        { label: 'MFA Setup',      color: 'text-teal-700',   bg: 'bg-teal-100'   },
  CONSENT_GIVEN:    { label: 'Consent Given',  color: 'text-green-700',  bg: 'bg-green-100'  },
  CONSENT_WITHDRAWN:{ label: 'Consent Revoked',color: 'text-amber-700',  bg: 'bg-amber-100'  },
};

const SOURCES = ['API', 'Auth', 'Database', 'Cron', 'Pharmacy', 'Lab', 'Billing', 'ABDM'];
const ACTION_TYPES = ['CREATE','UPDATE','DELETE','LOGIN','LOGOUT','LOGIN_FAILED','VIEW','DATA_EXPORT'];

// ── Small components ──────────────────────────────────────────────────────────
function LevelBadge({ level }: { level: Level }) {
  const cfg  = LEVEL_CONFIG[level] ?? LEVEL_CONFIG.INFO;
  const Icon = cfg.icon;
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold', cfg.bg, cfg.color)}>
      <Icon size={11}/>{cfg.label}
    </span>
  );
}

function ActionBadge({ action }: { action: string }) {
  const cfg = ACTION_CONFIG[action] ?? { label: action, color: 'text-gray-700', bg: 'bg-gray-100' };
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold', cfg.bg, cfg.color)}>
      {cfg.label}
    </span>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ msg, type, onClose }: { msg: string; type: 'error' | 'success'; onClose: () => void }) {
  return (
    <div className={clsx(
      'fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3',
      'px-5 py-3 rounded-xl shadow-xl text-sm font-medium border max-w-md w-full',
      type === 'error'
        ? 'bg-red-50 border-red-200 text-red-800'
        : 'bg-green-50 border-green-200 text-green-800',
    )}>
      {type === 'error' ? <AlertCircle size={16}/> : <Info size={16}/>}
      <span className="flex-1">{msg}</span>
      <button onClick={onClose} className="text-current opacity-60 hover:opacity-100"><X size={14}/></button>
    </div>
  );
}

// ── Sys-log detail modal ──────────────────────────────────────────────────────
function SysLogModal({ log, onClose }: { log: SysLog; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <LevelBadge level={log.level}/>
            <span className="text-xs font-mono text-gray-400">{log.source}</span>
            <span className="text-xs text-gray-400">{format(new Date(log.created_at), 'dd MMM yyyy HH:mm:ss.SSS')}</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={16}/></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Message</div>
            <p className="text-sm text-gray-800 font-medium">{log.message}</p>
          </div>
          {log.tenant_id && (
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Tenant</div>
              <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{log.tenant_id}</span>
            </div>
          )}
          {log.metadata && (
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Metadata</div>
              <pre className="text-xs bg-gray-50 border border-gray-200 rounded-xl p-3 overflow-x-auto font-mono text-gray-700">
                {JSON.stringify(log.metadata, null, 2)}
              </pre>
            </div>
          )}
          {log.stack_trace && (
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Stack Trace</div>
              <pre className="text-xs bg-red-50 border border-red-200 rounded-xl p-3 overflow-x-auto font-mono text-red-800 whitespace-pre-wrap">
                {log.stack_trace}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Audit event detail modal ──────────────────────────────────────────────────
function AuditModal({ event, onClose }: { event: AuditEvent; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <ActionBadge action={event.action_type}/>
            <span className="text-xs text-gray-500 font-medium">{event.resource_type}</span>
            <span className="text-xs text-gray-400">{format(new Date(event.created_at), 'dd MMM yyyy HH:mm:ss')}</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={16}/></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Actor</div>
              <p className="font-medium text-gray-800">{event.actor_name ?? event.actor_user_id}</p>
              <p className="text-xs text-gray-400 mt-0.5 capitalize">{event.actor_role.replace(/_/g,' ')}</p>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase mb-1">IP Address</div>
              <p className="font-mono text-gray-700">{event.actor_ip ?? '—'}</p>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Resource ID</div>
              <p className="font-mono text-xs text-gray-600 break-all">{event.resource_id}</p>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Tenant</div>
              <p className="text-gray-700">{event.tenant_name ?? event.tenant_id}</p>
            </div>
          </div>
          {!!event.metadata?.submitted && (
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Changes Submitted</div>
              <pre className="text-xs bg-slate-50 border border-slate-200 rounded-xl p-3 overflow-x-auto font-mono text-slate-700">
                {JSON.stringify(event.metadata!.submitted as object, null, 2)}
              </pre>
            </div>
          )}
          {!!event.metadata && !event.metadata.submitted && (
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Metadata</div>
              <pre className="text-xs bg-slate-50 border border-slate-200 rounded-xl p-3 overflow-x-auto font-mono text-slate-700">
                {JSON.stringify(event.metadata, null, 2)}
              </pre>
            </div>
          )}
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Event ID</div>
            <span className="font-mono text-xs text-gray-400">{event.event_id}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SystemLogsPage() {
  const today   = format(new Date(), 'yyyy-MM-dd');
  const weekAgo = format(subDays(new Date(), 7), 'yyyy-MM-dd');

  const [tab,       setTab]       = useState<Tab>('activity');

  // ── Activity log state ──────────────────────────────────────────
  const [aFrom,     setAFrom]     = useState(today);
  const [aTo,       setATo]       = useState(today);
  const [aAction,   setAAction]   = useState('');
  const [aSearch,   setASearch]   = useState('');
  const [aPage,     setAPage]     = useState(1);
  const [selEvent,  setSelEvent]  = useState<AuditEvent | null>(null);

  // ── System logs state ───────────────────────────────────────────
  const [from,      setFrom]      = useState(weekAgo);
  const [to,        setTo]        = useState(today);
  const [levels,    setLevels]    = useState<Level[]>(['ERROR', 'WARN']);
  const [source,    setSource]    = useState('');
  const [search,    setSearch]    = useState('');
  const [page,      setPage]      = useState(1);
  const [selLog,    setSelLog]    = useState<SysLog | null>(null);

  // ── Export / toast ──────────────────────────────────────────────
  const [exporting,   setExporting]   = useState(false);
  const [toast,       setToast]       = useState<{ msg: string; type: 'error' | 'success' } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const token = useAuthStore(s => s.accessToken) ?? '';

  function showToast(msg: string, type: 'error' | 'success' = 'error') {
    setToast({ msg, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }

  // ── Activity log query ──────────────────────────────────────────
  const aParams = useCallback(() => {
    const p: Record<string, string> = { from: aFrom, to: aTo, page: String(aPage), limit: '50' };
    if (aAction) p.action = aAction;
    if (aSearch) p.q      = aSearch;
    return new URLSearchParams(p).toString();
  }, [aFrom, aTo, aAction, aSearch, aPage]);

  const { data: aData, isLoading: aLoading, refetch: aRefetch, isFetching: aFetching } = useQuery({
    queryKey: ['audit-trail', aFrom, aTo, aAction, aSearch, aPage],
    queryFn:  () => api.get(`/admin/audit-trail?${aParams()}`).then(r => r.data),
    staleTime: 30_000,
    enabled:  tab === 'activity',
  });

  const events: AuditEvent[] = aData?.events ?? [];
  const aTotal = aData?.total ?? 0;
  const aPages = aData?.pages ?? 1;

  // ── System logs query ───────────────────────────────────────────
  const sParams = useCallback(() => {
    const p: Record<string, string> = { from, to, page: String(page), limit: '50' };
    if (levels.length === 1) p.level  = levels[0];
    if (source)              p.source = source;
    if (search)              p.q      = search;
    return new URLSearchParams(p).toString();
  }, [from, to, levels, source, search, page]);

  const { data: sData, isLoading: sLoading, refetch: sRefetch, isFetching: sFetching } = useQuery({
    queryKey: ['sys-logs', from, to, levels, source, search, page],
    queryFn:  () => api.get(`/admin/sys-logs?${sParams()}`).then(r => r.data),
    staleTime: 30_000,
    enabled:  tab === 'system',
  });

  const logs: SysLog[] = sData?.logs ?? [];
  const sTotal = sData?.total ?? 0;
  const sPages = sData?.pages ?? 1;

  function toggleLevel(l: Level) {
    setLevels(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);
    setPage(1);
  }

  // ── Export handler ──────────────────────────────────────────────
  async function handleExport() {
    const fromMs  = new Date(aFrom + 'T00:00:00').getTime();
    const toMs    = new Date(aTo   + 'T23:59:59').getTime();
    const diffHrs = (toMs - fromMs) / (1000 * 60 * 60);

    if (diffHrs > 24) {
      showToast(
        'To ensure system stability, please select a time range of 24 hours or less for downloads.',
        'error'
      );
      return;
    }

    setExporting(true);
    try {
      const url = `/api/v1/admin/audit-trail/export?from=${aFrom}T00:00:00&to=${aTo}T23:59:59`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as any;
        throw new Error(body.error ?? `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a    = Object.assign(document.createElement('a'), {
        href,
        download: `audit-trail-${aFrom}.csv`,
      });
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
      showToast('Export downloaded successfully.', 'success');
    } catch (e: any) {
      showToast(e.message ?? 'Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="p-6 space-y-5 max-w-screen-xl mx-auto">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-navy/10 flex items-center justify-center">
            <Activity size={18} className="text-navy"/>
          </div>
          <div>
            <h1 className="text-xl font-bold text-navy">Audit &amp; Activity Log</h1>
            <p className="text-xs text-gray-400 mt-0.5">Complete record of all system activity</p>
          </div>
        </div>
        <button
          onClick={() => tab === 'activity' ? aRefetch() : sRefetch()}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw size={14} className={(aFetching || sFetching) ? 'animate-spin' : ''}/>
          Refresh
        </button>
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setTab('activity')}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            tab === 'activity'
              ? 'bg-white text-navy shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <Shield size={14}/>
          Activity Log
          {aTotal > 0 && tab === 'activity' && (
            <span className="ml-1 bg-navy/10 text-navy text-xs px-1.5 py-0.5 rounded-full">
              {aTotal.toLocaleString()}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('system')}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            tab === 'system'
              ? 'bg-white text-navy shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <Layers size={14}/>
          System Events
          {sTotal > 0 && tab === 'system' && (
            <span className="ml-1 bg-navy/10 text-navy text-xs px-1.5 py-0.5 rounded-full">
              {sTotal.toLocaleString()}
            </span>
          )}
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  ACTIVITY LOG TAB                                           */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {tab === 'activity' && (
        <>
          {/* Filters */}
          <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm space-y-3">
            <div className="flex flex-wrap gap-3 items-end">
              {/* Date range */}
              <div className="flex items-center gap-2">
                <input type="date" value={aFrom}
                  onChange={e => { setAFrom(e.target.value); setAPage(1); }}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy/40"
                />
                <span className="text-gray-400 text-sm">to</span>
                <input type="date" value={aTo}
                  onChange={e => { setATo(e.target.value); setAPage(1); }}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy/40"
                />
              </div>

              {/* Action filter */}
              <select value={aAction}
                onChange={e => { setAAction(e.target.value); setAPage(1); }}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy/40 bg-white"
              >
                <option value="">All actions</option>
                {ACTION_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
              </select>

              {/* Search */}
              <div className="flex items-center gap-2 flex-1 min-w-[220px] border border-gray-200 rounded-xl px-3 py-2 bg-white">
                <Search size={14} className="text-gray-400 flex-shrink-0"/>
                <input
                  value={aSearch}
                  onChange={e => { setASearch(e.target.value); setAPage(1); }}
                  placeholder="Search by user, resource or action…"
                  className="flex-1 text-sm outline-none text-gray-700 placeholder:text-gray-300"
                />
                {aSearch && (
                  <button onClick={() => setASearch('')} className="text-gray-300 hover:text-gray-500">
                    <X size={13}/>
                  </button>
                )}
              </div>

              {/* Export button */}
              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-navy text-white text-sm font-medium hover:bg-navy/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                {exporting
                  ? <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                  : <Download size={14}/>
                }
                {exporting ? 'Exporting…' : 'Download CSV'}
              </button>
            </div>
            <p className="text-xs text-gray-400">
              Showing events for <span className="font-medium text-gray-600">{aFrom}</span> to{' '}
              <span className="font-medium text-gray-600">{aTo}</span> ·{' '}
              Export is limited to a 24-hour window per download.
            </p>
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase w-36">Time</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">User</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase w-32">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Resource</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase w-28">Tenant</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase w-28">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {aLoading ? (
                  <tr><td colSpan={6} className="text-center py-16 text-gray-400">Loading…</td></tr>
                ) : events.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-16">
                      <Shield size={36} className="mx-auto text-gray-200 mb-3"/>
                      <p className="text-gray-400 text-sm">No activity for the selected filters</p>
                    </td>
                  </tr>
                ) : events.map(ev => (
                  <tr
                    key={ev.event_id}
                    onClick={() => setSelEvent(ev)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                      {format(new Date(ev.created_at), 'dd MMM HH:mm:ss')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0">
                          <User size={11} className="text-navy"/>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800 leading-tight">
                            {ev.actor_name ?? ev.actor_user_id.slice(0, 8) + '…'}
                          </p>
                          <p className="text-xs text-gray-400 capitalize leading-tight">
                            {ev.actor_role.replace(/_/g, ' ')}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <ActionBadge action={ev.action_type}/>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-gray-700 capitalize">
                        {ev.resource_type.replace(/_/g, ' ')}
                      </span>
                      <p className="text-xs text-gray-400 font-mono truncate max-w-[160px]">
                        {ev.resource_id}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 truncate max-w-[100px]">
                      {ev.tenant_name ?? ev.tenant_id.slice(0, 8) + '…'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 font-mono">
                      {ev.actor_ip ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {aPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Page {aPage} of {aPages} · {aTotal} events</span>
              <div className="flex gap-2">
                <button onClick={() => setAPage(p => Math.max(1, p - 1))} disabled={aPage === 1}
                  className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                  <ChevronLeft size={16}/>
                </button>
                <button onClick={() => setAPage(p => Math.min(aPages, p + 1))} disabled={aPage === aPages}
                  className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                  <ChevronRight size={16}/>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  SYSTEM EVENTS TAB (existing sys_logs view)                 */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {tab === 'system' && (
        <>
          {/* Summary chips */}
          {(() => {
            const ec = logs.filter(l => l.level === 'ERROR').length;
            const wc = logs.filter(l => l.level === 'WARN').length;
            return (ec > 0 || wc > 0) ? (
              <div className="flex gap-3">
                {ec > 0 && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 font-medium">
                    <AlertCircle size={14}/> {ec} error{ec > 1 ? 's' : ''} on this page
                  </div>
                )}
                {wc > 0 && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-700 font-medium">
                    <AlertTriangle size={14}/> {wc} warning{wc > 1 ? 's' : ''} on this page
                  </div>
                )}
              </div>
            ) : null;
          })()}

          {/* Filters */}
          <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm space-y-3">
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <input type="date" value={from}
                  onChange={e => { setFrom(e.target.value); setPage(1); }}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy/40"
                />
                <span className="text-gray-400 text-sm">to</span>
                <input type="date" value={to}
                  onChange={e => { setTo(e.target.value); setPage(1); }}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy/40"
                />
              </div>
              <select value={source} onChange={e => { setSource(e.target.value); setPage(1); }}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy/40 bg-white">
                <option value="">All sources</option>
                {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <div className="flex items-center gap-2 flex-1 min-w-[220px] border border-gray-200 rounded-xl px-3 py-2 bg-white">
                <Search size={14} className="text-gray-400 flex-shrink-0"/>
                <input value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search message or source…"
                  className="flex-1 text-sm outline-none text-gray-700 placeholder:text-gray-300"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="text-gray-300 hover:text-gray-500"><X size={13}/></button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-400 font-medium">Level:</span>
              {(['ERROR','WARN','INFO','DEBUG'] as Level[]).map(l => {
                const cfg  = LEVEL_CONFIG[l];
                const Icon = cfg.icon;
                const on   = levels.includes(l);
                return (
                  <button key={l} onClick={() => toggleLevel(l)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-all',
                      on ? `${cfg.bg} ${cfg.color} border-transparent` : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'
                    )}>
                    <Icon size={11}/>{cfg.label}
                  </button>
                );
              })}
              <span className="ml-auto text-xs text-gray-400">{sTotal.toLocaleString()} total</span>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase w-28">Level</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase w-24">Source</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Message</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase w-32">Tenant</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase w-40">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sLoading ? (
                  <tr><td colSpan={5} className="text-center py-16 text-gray-400">Loading…</td></tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-16">
                      <Activity size={36} className="mx-auto text-gray-200 mb-3"/>
                      <p className="text-gray-400 text-sm">No logs found for the selected filters</p>
                    </td>
                  </tr>
                ) : logs.map(log => (
                  <tr key={log.id} onClick={() => setSelLog(log)}
                    className={clsx(
                      'hover:bg-gray-50 cursor-pointer transition-colors',
                      log.level === 'ERROR' && 'bg-red-50/40 hover:bg-red-50/60',
                      log.level === 'WARN'  && 'bg-yellow-50/30 hover:bg-yellow-50/50',
                    )}>
                    <td className="px-4 py-3"><LevelBadge level={log.level}/></td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{log.source}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-xs">
                      <p className="truncate text-sm">{log.message}</p>
                      {log.stack_trace && <span className="text-xs text-red-400 font-medium">has stack trace</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 font-mono truncate max-w-[120px]">
                      {log.tenant_id ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                      {format(new Date(log.created_at), 'dd MMM HH:mm:ss')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {sPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Page {page} of {sPages} · {sTotal} entries</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                  <ChevronLeft size={16}/>
                </button>
                <button onClick={() => setPage(p => Math.min(sPages, p + 1))} disabled={page === sPages}
                  className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                  <ChevronRight size={16}/>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Modals ─────────────────────────────────────────────────── */}
      {selEvent && <AuditModal event={selEvent} onClose={() => setSelEvent(null)}/>}
      {selLog   && <SysLogModal log={selLog}    onClose={() => setSelLog(null)}/>}

      {/* ── Toast ──────────────────────────────────────────────────── */}
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)}/>}
    </div>
  );
}
